import { getConversionFactorValue } from '@shared/helpers/conversion-factors.helpers';
import { getDesignExpressionVariables } from '@shared/helpers/design/expressions/design-expression-variables.helpers';
import { sumStoreysFromDesignExpressionVariables } from '@shared/helpers/design/expressions/storey-sum-from-design.helpers';
import {
  resolveBoverketMaterialCategoryId,
  resolveBoverketOtherMaterialGroupLabel,
} from './boverket-climate-declaration-material.helpers';
import {
  buildBoverketClimateImpactFieldsFromRecord,
  type BoverketClimateImpactFields,
} from './boverket-climate-declaration-results.helpers';
import { isElementActiveInProposal } from '@shared/helpers/proposal.helpers';
import {
  getEPDProductId,
  getGenericProductId,
} from '@shared/helpers/product-element.helpers';
import {
  getResultsById,
  getResultsRecord,
} from '@shared/helpers/results.helpers';
import {
  getSbefCodeForProductElement,
  getSbefGroupCode,
} from '@shared/helpers/sbef.helpers';
import { SbefGroupCode } from '@shared/construction/sbef.model';
import { ActivityType } from '@shared/models/design/activities/activity-type.model';
import { BuildingDesign } from '@shared/models/design/building-design.model';
import { ElementCategoryID } from '@shared/models/element-categories.model';
import type { IProduct } from '@shared/models/product.model';
import {
  type Baseline,
  type IProductElement,
} from '@shared/models/project.model';
import type { IProposal } from '@shared/models/proposals.model';
import {
  forEachElement,
  isProductElement,
} from '@shared/helpers/recursive-element.helpers';
import { emptyStoreySummedValues } from '@shared/helpers/storeys.helpers';

export type { BoverketClimateImpactFields } from './boverket-climate-declaration-results.helpers';

type MaterialMassBySlot = {
  concrete: number;
  steel: number;
  wood: number;
  other: Map<string, number>;
};

export type BoverketVersionExportContext = {
  design: BuildingDesign;
  version?: Baseline;
  proposal?: IProposal;
};

export type BoverketSpecialAreas = {
  solcellerArea: number;
  loftgangarBTA: number;
  loftgangarOPA: number;
  balkongOPA: number;
};

export type BoverketMaterialShareFields = {
  barandekonstruktionGrundlaggningBetong: number;
  barandekonstruktionGrundlaggningStal: number;
  barandekonstruktionGrundlaggningTra: number;
  barandekonstruktionGrundlaggningOvrigt1Procent: number;
  barandekonstruktionGrundlaggningOvrigt2Procent: number;
  barandekonstruktionGrundlaggningOvrigt1Konstruktionsdel: string;
  barandekonstruktionGrundlaggningOvrigt2Konstruktionsdel: string;
  barandekonstruktionOvrigaBetong: number;
  barandekonstruktionOvrigaStal: number;
  barandekonstruktionOvrigaTra: number;
  barandekonstruktionOvrigaOvrigt1Procent: number;
  barandekonstruktionOvrigaOvrigt1Konstruktionsdel: string;
  barandekonstruktionOvrigaOvrigt2Procent: number;
  barandekonstruktionOvrigaOvrigt2Konstruktionsdel: string;
};

/**
 * Garage GFA from design program geometry (not NIA share).
 */
export const getGarageAreaFromDesign = (design: BuildingDesign): number => {
  const designVariables = getDesignExpressionVariables(design);
  if (!designVariables) return 0;
  return Math.round(designVariables.programs[ActivityType.Garage]?.gfa ?? 0);
};

/**
 * Storey counts excluding mezzanine levels (uses design storey flags + enriched GFA).
 */
export const countNonMezzanineStoreys = (
  design: BuildingDesign,
  filter: (enriched: {
    gfa_above_ground: number;
    gfa_below_ground: number;
  }) => boolean,
): number => {
  const designVariables = getDesignExpressionVariables(design);
  if (!designVariables) return 0;
  const { storeys } = sumStoreysFromDesignExpressionVariables(designVariables);
  return designVariables.storeys.reduce((count, storey, index) => {
    if (storey.mezzanine) return count;
    const enriched = storeys[index];
    if (!enriched || !filter(enriched)) return count;
    return count + 1;
  }, 0);
};

const getProductForElement = (
  version: Baseline,
  productElement: IProductElement,
): IProduct | undefined => {
  const productId =
    getEPDProductId(productElement) ?? getGenericProductId(productElement);
  return version.products[productId];
};

const createMaterialMassAccumulator = (): MaterialMassBySlot => ({
  concrete: 0,
  steel: 0,
  wood: 0,
  other: new Map(),
});

const addMaterialMass = (
  acc: MaterialMassBySlot,
  categoryId: ElementCategoryID | undefined,
  massKg: number,
  otherMaterialLabel: string,
): void => {
  if (massKg <= 0) return;
  if (categoryId === ElementCategoryID.Concrete) {
    acc.concrete += massKg;
    return;
  }
  if (categoryId === ElementCategoryID.Metal) {
    acc.steel += massKg;
    return;
  }
  if (categoryId === ElementCategoryID.Wood) {
    acc.wood += massKg;
    return;
  }
  const label = otherMaterialLabel.trim() || 'Övrigt';
  acc.other.set(label, (acc.other.get(label) ?? 0) + massKg);
};

const toMaterialShareFields = (
  mass: MaterialMassBySlot,
): Pick<
  BoverketMaterialShareFields,
  | 'barandekonstruktionGrundlaggningBetong'
  | 'barandekonstruktionGrundlaggningStal'
  | 'barandekonstruktionGrundlaggningTra'
  | 'barandekonstruktionGrundlaggningOvrigt1Procent'
  | 'barandekonstruktionGrundlaggningOvrigt2Procent'
  | 'barandekonstruktionGrundlaggningOvrigt1Konstruktionsdel'
  | 'barandekonstruktionGrundlaggningOvrigt2Konstruktionsdel'
> => {
  const total =
    mass.concrete +
    mass.steel +
    mass.wood +
    [...mass.other.values()].reduce((a, b) => a + b, 0);
  if (total <= 0) {
    return {
      barandekonstruktionGrundlaggningBetong: 0,
      barandekonstruktionGrundlaggningStal: 0,
      barandekonstruktionGrundlaggningTra: 0,
      barandekonstruktionGrundlaggningOvrigt1Procent: 0,
      barandekonstruktionGrundlaggningOvrigt2Procent: 0,
      barandekonstruktionGrundlaggningOvrigt1Konstruktionsdel: '',
      barandekonstruktionGrundlaggningOvrigt2Konstruktionsdel: '',
    };
  }

  const percent = (part: number) => Math.round((part / total) * 100);
  const sortedOther = [...mass.other.entries()].sort((a, b) => b[1] - a[1]);
  const [other1, other2] = sortedOther;
  const ovrigt1Procent = other1 ? percent(other1[1]) : 0;
  const ovrigt2Procent = other2 ? percent(other2[1]) : 0;

  return {
    barandekonstruktionGrundlaggningBetong: percent(mass.concrete),
    barandekonstruktionGrundlaggningStal: percent(mass.steel),
    barandekonstruktionGrundlaggningTra: percent(mass.wood),
    barandekonstruktionGrundlaggningOvrigt1Procent: ovrigt1Procent,
    barandekonstruktionGrundlaggningOvrigt2Procent: ovrigt2Procent,
    barandekonstruktionGrundlaggningOvrigt1Konstruktionsdel:
      ovrigt1Procent > 0 ? (other1?.[0] ?? '') : '',
    barandekonstruktionGrundlaggningOvrigt2Konstruktionsdel:
      ovrigt2Procent > 0 ? (other2?.[0] ?? '') : '',
  };
};

/**
 * Special building areas and material shares from version products + design storey sums.
 */
export const buildBoverketVersionDerivedBuildingFields = ({
  design,
  version,
  proposal,
}: BoverketVersionExportContext): BoverketSpecialAreas &
  BoverketMaterialShareFields => {
  const emptySpecial: BoverketSpecialAreas = {
    solcellerArea: 0,
    loftgangarBTA: 0,
    loftgangarOPA: 0,
    balkongOPA: 0,
  };
  const emptyMaterial: BoverketMaterialShareFields = {
    ...toMaterialShareFields(createMaterialMassAccumulator()),
    barandekonstruktionOvrigaBetong: 0,
    barandekonstruktionOvrigaStal: 0,
    barandekonstruktionOvrigaTra: 0,
    barandekonstruktionOvrigaOvrigt1Procent: 0,
    barandekonstruktionOvrigaOvrigt1Konstruktionsdel: '',
    barandekonstruktionOvrigaOvrigt2Procent: 0,
    barandekonstruktionOvrigaOvrigt2Konstruktionsdel: '',
  };

  if (!version) {
    return { ...emptySpecial, ...emptyMaterial };
  }

  const resultsRecord = getResultsRecord(version, proposal);
  const designVariables = getDesignExpressionVariables(design);
  const { sum } = designVariables
    ? sumStoreysFromDesignExpressionVariables(designVariables)
    : { sum: { ...emptyStoreySummedValues } };

  const grundMass = createMaterialMassAccumulator();
  const ovrigaMass = createMaterialMassAccumulator();

  forEachElement(version, (element, path) => {
    if (!isProductElement(element)) return;
    if (!isElementActiveInProposal(proposal, path, element)) return;

    const productResults = getResultsById(resultsRecord, element.id);
    const sbef = getSbefCodeForProductElement(element, path);
    const product = getProductForElement(version, element);
    const massKg = getConversionFactorValue(productResults, 'kg');
    const categoryId = resolveBoverketMaterialCategoryId(
      element,
      path,
      product,
    );
    const otherMaterialLabel = resolveBoverketOtherMaterialGroupLabel(
      element,
      path,
      product,
    );

    const group = getSbefGroupCode(sbef);
    if (group === SbefGroupCode.HouseSubstructure) {
      addMaterialMass(grundMass, categoryId, massKg, otherMaterialLabel);
    }
    if (
      group === SbefGroupCode.Framework ||
      group === SbefGroupCode.FrameCompletionRoomForm
    ) {
      addMaterialMass(ovrigaMass, categoryId, massKg, otherMaterialLabel);
    }
  });

  const grundShares = toMaterialShareFields(grundMass);
  const ovrigaShares = toMaterialShareFields(ovrigaMass);

  return {
    solcellerArea: 0,
    loftgangarBTA: 0,
    // No dedicated OPA geometry — terrace slab area is the closest proxy for open gallery decks.
    loftgangarOPA: Math.round(sum.gfa_terrace_slabs ?? 0),
    balkongOPA: Math.round(sum.gfa_balconies ?? 0),
    ...grundShares,
    barandekonstruktionOvrigaBetong:
      ovrigaShares.barandekonstruktionGrundlaggningBetong,
    barandekonstruktionOvrigaStal:
      ovrigaShares.barandekonstruktionGrundlaggningStal,
    barandekonstruktionOvrigaTra:
      ovrigaShares.barandekonstruktionGrundlaggningTra,
    barandekonstruktionOvrigaOvrigt1Procent:
      ovrigaShares.barandekonstruktionGrundlaggningOvrigt1Procent,
    barandekonstruktionOvrigaOvrigt1Konstruktionsdel:
      ovrigaShares.barandekonstruktionGrundlaggningOvrigt1Konstruktionsdel,
    barandekonstruktionOvrigaOvrigt2Procent:
      ovrigaShares.barandekonstruktionGrundlaggningOvrigt2Procent,
    barandekonstruktionOvrigaOvrigt2Konstruktionsdel:
      ovrigaShares.barandekonstruktionGrundlaggningOvrigt2Konstruktionsdel,
  };
};

/**
 * Climate declaration impact fields from version results (GK/SK split + coverage).
 * @param conservative When true, the stored per-product conservative A1–A3 delta (Boverket
 *   ConservativeDataConversionFactor) is added to generic A1–A3 for GK.
 */
export const buildBoverketClimateImpactFieldsFromVersion = ({
  version,
  proposal,
  conservative = true,
}: Pick<BoverketVersionExportContext, 'version' | 'proposal'> & {
  conservative?: boolean;
}): BoverketClimateImpactFields => {
  if (!version) {
    return {
      barandekonstruktionsdelarGrundlaggningGK: 0,
      barandekonstruktionsdelarGrundlaggningSK: 0,
      barandekonstruktionsdelarOvrigaGK: 0,
      barandekonstruktionsdelarOvrigaSK: 0,
      klimatskarmGK: 0,
      klimatskarmSK: 0,
      innervaggarGK: 0,
      innervaggarSK: 0,
      a4TransportGK: 0,
      a4TransportSK: 0,
      a5ByggspillGK: 0,
      a5ByggspillSK: 0,
      energiPaverkan: 0,
      upprakningAvBeraknadKlimatpaverkan: 0,
      tackningsgrad: 0,
    };
  }

  return buildBoverketClimateImpactFieldsFromRecord(
    version,
    getResultsRecord(version, proposal),
    { proposal, conservative },
  );
};
